import { useEffect, useRef, useState, type PointerEvent } from 'react';
import { EXIT } from '@/ui/motion/motion';
import { CheckIcon } from '@/ui/icons';
import styles from './Toast.module.css';
import { useToastStore, type ToastItem } from './toastStore';

/** Long enough to read twice; a toast with an action stays a little longer to be pressed. */
export const VISIBLE_MS = 3500;
export const VISIBLE_WITH_ACTION_MS = 5000;
/** How far a toast is pushed sideways before letting go dismisses it. */
const SWIPE_PX = 60;

/**
 * Section 13 asks for `aria-live` on toasts. The region is always mounted and
 * always polite: a live region added to the DOM at the same moment as its
 * content is not reliably announced by a screen reader.
 *
 * On a phone the stack sits at the top, under the header, where it never
 * covers the task bar or the confirm of a sheet at the bottom of the screen.
 */
export function Toast() {
  const items = useToastStore((state) => state.items);

  return (
    <div className={styles.stack} role="status" aria-live="polite">
      {items.map((item) => (
        <ToastCard key={item.id} item={item} />
      ))}
    </div>
  );
}

function ToastCard({ item }: { item: ToastItem }) {
  const remove = useToastStore((state) => state.dismiss);
  const [leaving, setLeaving] = useState(false);
  // Slides away, a little quicker than it came, then goes. Section 6, 5 October 2026.
  const dismiss = (id: number) => {
    if (leaving) return;
    setLeaving(true);
    setTimeout(() => remove(id), EXIT.fast);
  };
  const card = useRef<HTMLDivElement>(null);
  const start = useRef<number | null>(null);
  const moved = useRef(0);

  useEffect(() => {
    const timer = setTimeout(
      () => {
        setLeaving(true);
        setTimeout(() => remove(item.id), EXIT.fast);
      },
      item.durationMs ?? (item.action === null ? VISIBLE_MS : VISIBLE_WITH_ACTION_MS),
    );
    return () => clearTimeout(timer);
  }, [item.id, item.action, item.durationMs, remove]);

  function down(event: PointerEvent<HTMLDivElement>) {
    if ((event.target as HTMLElement).closest('button') !== null) return;
    start.current = event.clientX;
    event.currentTarget.setPointerCapture?.(event.pointerId);
  }

  function move(event: PointerEvent<HTMLDivElement>) {
    if (start.current === null || card.current === null) return;
    moved.current = event.clientX - start.current;
    card.current.style.transform = `translateX(${String(moved.current)}px)`;
    card.current.style.opacity = String(Math.max(0.2, 1 - Math.abs(moved.current) / 200));
  }

  function up() {
    if (start.current === null || card.current === null) return;
    start.current = null;
    if (Math.abs(moved.current) > SWIPE_PX) {
      dismiss(item.id);
      return;
    }
    moved.current = 0;
    card.current.style.transform = '';
    card.current.style.opacity = '';
  }

  return (
    <div
      ref={card}
      className={`${styles.toast} ${styles[item.tone]} ${leaving ? styles.leaving : ''}`}
      onPointerDown={down}
      onPointerMove={move}
      onPointerUp={up}
      onPointerCancel={up}
    >
      {item.tone === 'success' && (
        <CheckIcon size={16} className={styles.mark} aria-hidden="true" />
      )}
      {item.tone === 'error' && (
        <span className={styles.mark} aria-hidden="true">
          !
        </span>
      )}
      <span className={styles.text}>{item.message}</span>
      {item.action !== null && (
        <button
          type="button"
          className={styles.action}
          onClick={() => {
            item.action?.onClick();
            dismiss(item.id);
          }}
        >
          {item.action.label}
        </button>
      )}
    </div>
  );
}
